import React, { useState, useEffect, useCallback } from 'react';
import { maskingAPI } from '../../services/api';
import {
  Fingerprint, User, Search, Shield, Eye, EyeOff, Save, Loader2, Check, X,
  DollarSign, KeyRound, Hash, Type, FileText, Phone, Plus, Trash2, ChevronDown
} from 'lucide-react';

const MOBILE_MASK_PRESETS = [
  { label: '958xx6xx43', pattern: 'XXXxxXxxXX', desc: 'Show first 3, last 2' },
  { label: '958xxxx143', pattern: 'XXXxxxxXXX', desc: 'Show first 3, last 3' },
  { label: '9xxxx68143', pattern: 'XxxxxXXXXX', desc: 'Show first 1, last 5' },
  { label: 'xxxxxx8143', pattern: 'xxxxxxXXXX', desc: 'Show last 4 only' },
  { label: '95865xxx43', pattern: 'XXXXXxxxXX', desc: 'Show first 5, last 2' },
];

const MaskingManagement = () => {
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [maskingUserSearch, setMaskingUserSearch] = useState('');

  // Settings state
  const [settings, setSettings] = useState(null);
  const [templates, setTemplates] = useState([]);

  // Form state
  const [maskingEnabled, setMaskingEnabled] = useState(false);
  const [amountMask, setAmountMask] = useState(false);
  const [otpMask, setOtpMask] = useState(false);
  const [digitMask, setDigitMask] = useState(false);
  const [customCharMask, setCustomCharMask] = useState(false);
  const [customChars, setCustomChars] = useState('');
  const [templateVarMask, setTemplateVarMask] = useState(false);
  const [mobileMask, setMobileMask] = useState(false);
  const [mobileMaskType, setMobileMaskType] = useState('middle');
  const [mobileMaskPattern, setMobileMaskPattern] = useState('XXXxxXxxXX');

  // Template add state
  const [newTemplateId, setNewTemplateId] = useState('');
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateVars, setNewTemplateVars] = useState('');
  const [addingTemplate, setAddingTemplate] = useState(false);

  // Load masking users list
  const fetchMaskingUsers = useCallback(async () => {
    try {
      const res = await maskingAPI.getAllUsers();
      setUsers(res.data.users || []);
    } catch (err) { /* ignore */ }
  }, []);

  // Load all users for dropdown
  useEffect(() => {
    setLoading(true);
    const token = localStorage.getItem('token');
    fetch('/api/v1/admin/user-crm/list', {
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
    })
      .then(r => r.json())
      .then(data => { setAllUsers(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
    fetchMaskingUsers();
  }, [fetchMaskingUsers]);

  const selectUser = async (user) => {
    setSelectedUser(user);
    setDropdownOpen(false);
    setSearchTerm('');
    setError('');
    setSuccess('');
    try {
      const res = await maskingAPI.getSettings(user.id);
      const s = res.data.settings;
      setSettings(s);
      setTemplates(res.data.templates || []);
      setMaskingEnabled(s.masking_enabled || false);
      setAmountMask(s.amount_mask || false);
      setOtpMask(s.otp_mask || false);
      setDigitMask(s.digit_mask || false);
      setCustomCharMask(s.custom_char_mask || false);
      setCustomChars(s.custom_chars || '');
      setTemplateVarMask(s.template_var_mask || false);
      setMobileMask(s.mobile_mask || false);
      setMobileMaskType(s.mobile_mask_type || 'middle');
      setMobileMaskPattern(s.mobile_mask_pattern || 'XXXxxXxxXX');
    } catch (err) {
      setError('Failed to load masking settings');
    }
  };

  const handleSave = async () => {
    if (!selectedUser) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await maskingAPI.updateSettings(selectedUser.id, {
        masking_enabled: maskingEnabled,
        amount_mask: amountMask,
        otp_mask: otpMask,
        digit_mask: digitMask,
        custom_char_mask: customCharMask,
        custom_chars: customChars,
        template_var_mask: templateVarMask,
        mobile_mask: mobileMask,
        mobile_mask_type: mobileMaskType,
        mobile_mask_pattern: mobileMaskPattern,
      });
      setSuccess('Masking settings saved successfully');
      fetchMaskingUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleAddTemplate = async () => {
    if (!selectedUser || !newTemplateId.trim()) return;
    setAddingTemplate(true);
    try {
      await maskingAPI.addTemplate(selectedUser.id, {
        template_id: newTemplateId.trim(),
        template_name: newTemplateName.trim(),
        masked_variables: newTemplateVars.split(',').map(v => v.trim()).filter(Boolean),
      });
      setNewTemplateId('');
      setNewTemplateName('');
      setNewTemplateVars('');
      // Reload
      const res = await maskingAPI.getSettings(selectedUser.id);
      setTemplates(res.data.templates || []);
      setSuccess('Template added');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to add template');
    } finally {
      setAddingTemplate(false);
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Remove this template masking?')) return;
    try {
      await maskingAPI.deleteTemplate(id);
      setTemplates(templates.filter(t => t.id !== id));
      setSuccess('Template removed');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to remove template');
    }
  };

  const filteredUsers = allUsers.filter(u =>
    !searchTerm || u.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (u.full_name && u.full_name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Preview masking
  const previewMobile = (pattern) => {
    const sample = '9586568143';
    return sample.split('').map((ch, i) => {
      if (i < pattern.length && pattern[i].toLowerCase() === 'x') return 'x';
      return ch;
    }).join('');
  };

  const colorMap = {
    indigo: '#6366f1', green: '#22c55e', blue: '#3b82f6', amber: '#f59e0b',
    pink: '#ec4899', purple: '#a855f7', red: '#ef4444',
  };

  const Toggle = ({ checked, onChange, label, icon: Icon, color = 'indigo' }) => (
    <div className="flex items-center justify-between p-3 rounded-lg border border-gray-100 hover:border-gray-200 transition-colors">
      <div className="flex items-center gap-2.5">
        {Icon && <Icon size={16} style={{ color: colorMap[color] || colorMap.indigo }} />}
        <span className="text-sm font-medium text-gray-700">{label}</span>
      </div>
      <button onClick={() => onChange(!checked)}
        className="relative inline-flex h-5 w-9 items-center rounded-full transition-colors"
        style={{ backgroundColor: checked ? (colorMap[color] || colorMap.indigo) : '#d1d5db' }}>
        <span className="inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform"
          style={{ transform: checked ? 'translateX(18px)' : 'translateX(2px)' }} />
      </button>
    </div>
  );

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center">
            <Fingerprint size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Content Masking Management</h1>
            <p className="text-xs text-gray-500">Configure SMS content masking per user (GUI display only)</p>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="flex items-center gap-2 px-4 py-3 mb-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
          <X size={16} /> {error}
          <button onClick={() => setError('')} className="ml-auto"><X size={14} /></button>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 px-4 py-3 mb-4 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm">
          <Check size={16} /> {success}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left: User selector + masking users list */}
        <div className="lg:col-span-1 space-y-4">
          {/* User Dropdown */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Select User</label>
            <div className="relative">
              <button onClick={() => setDropdownOpen(!dropdownOpen)}
                className="w-full flex items-center justify-between px-3 py-2.5 border border-gray-200 rounded-lg text-sm hover:border-gray-300">
                <span className={selectedUser ? 'text-gray-800 font-medium' : 'text-gray-400'}>
                  {selectedUser ? selectedUser.username : 'Choose user...'}
                </span>
                <ChevronDown size={16} className="text-gray-400" />
              </button>
              {dropdownOpen && (
                <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-hidden">
                  <div className="p-2 border-b border-gray-100">
                    <div className="relative">
                      <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search..."
                        className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none" autoFocus />
                    </div>
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    {filteredUsers.map(u => (
                      <button key={u.id} onClick={() => selectUser(u)}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${selectedUser?.id === u.id ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700'}`}>
                        <div className="font-medium">{u.username}</div>
                        <div className="text-[10px] text-gray-400">{u.full_name || u.email || u.role}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Masking Users List */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Users with Masking</h3>
              <span className="text-[10px] text-gray-400">{users.length}</span>
            </div>
            {users.length > 3 && (
              <div className="relative mb-3">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input value={maskingUserSearch} onChange={e => setMaskingUserSearch(e.target.value)} placeholder="Search masking users..."
                  className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-indigo-400" />
              </div>
            )}
            {users.length > 0 ? (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {users.filter(u => !maskingUserSearch || u.username.toLowerCase().includes(maskingUserSearch.toLowerCase()) || (u.full_name && u.full_name.toLowerCase().includes(maskingUserSearch.toLowerCase()))).map(u => (
                  <div key={u.user_id}
                    onClick={() => selectUser({ id: u.user_id, username: u.username, full_name: u.full_name })}
                    className={`p-2.5 rounded-lg border cursor-pointer transition-colors ${
                      selectedUser?.id === u.user_id ? 'border-indigo-300 bg-indigo-50' : 'border-gray-100 hover:border-gray-200'
                    }`}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-800">{u.username}</span>
                      {u.masking_enabled ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-violet-100 text-violet-700">
                          <Eye size={10} /> ON
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-500">
                          <EyeOff size={10} /> OFF
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {u.amount_mask && <span className="text-[9px] px-1 py-0.5 rounded bg-green-50 text-green-600">Amount</span>}
                      {u.otp_mask && <span className="text-[9px] px-1 py-0.5 rounded bg-blue-50 text-blue-600">OTP</span>}
                      {u.digit_mask && <span className="text-[9px] px-1 py-0.5 rounded bg-amber-50 text-amber-600">Digit</span>}
                      {u.custom_char_mask && <span className="text-[9px] px-1 py-0.5 rounded bg-pink-50 text-pink-600">Custom</span>}
                      {u.template_var_mask && <span className="text-[9px] px-1 py-0.5 rounded bg-purple-50 text-purple-600">Template</span>}
                      {u.mobile_mask && <span className="text-[9px] px-1 py-0.5 rounded bg-red-50 text-red-600">Mobile</span>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-400 text-center py-4">No users with masking configured</p>
            )}
          </div>
        </div>

        {/* Right: Settings panel */}
        <div className="lg:col-span-3">
          {!selectedUser ? (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <Fingerprint size={48} className="text-gray-200 mx-auto mb-3" />
              <p className="text-gray-400">Select a user to configure content masking</p>
            </div>
          ) : (
            <div className="space-y-5">
              {/* Master toggle */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${maskingEnabled ? 'bg-violet-100' : 'bg-gray-100'}`}>
                      <Fingerprint size={20} className={maskingEnabled ? 'text-violet-600' : 'text-gray-400'} />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-gray-800">Content Masking for {selectedUser.username}</h3>
                      <p className="text-xs text-gray-500">Masks sensitive data in SMS content on GUI display only</p>
                    </div>
                  </div>
                  <button onClick={() => setMaskingEnabled(!maskingEnabled)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${maskingEnabled ? 'bg-violet-600' : 'bg-gray-300'}`}>
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                      style={{ transform: maskingEnabled ? 'translateX(22px)' : 'translateX(4px)' }} />
                  </button>
                </div>
              </div>

              {maskingEnabled && (
                <>
                  {/* Masking Types */}
                  <div className="bg-white rounded-xl border border-gray-200 p-5">
                    <h3 className="text-sm font-bold text-gray-800 mb-4">Masking Types</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Toggle checked={amountMask} onChange={setAmountMask} label="Amount Masking" icon={DollarSign} color="green" />
                      <Toggle checked={otpMask} onChange={setOtpMask} label="OTP Masking" icon={KeyRound} color="blue" />
                      <Toggle checked={digitMask} onChange={setDigitMask} label="Digit Masking" icon={Hash} color="amber" />
                      <Toggle checked={mobileMask} onChange={setMobileMask} label="Mobile Number Masking" icon={Phone} color="red" />
                      <Toggle checked={customCharMask} onChange={setCustomCharMask} label="Custom Character Masking" icon={Type} color="pink" />
                      <Toggle checked={templateVarMask} onChange={setTemplateVarMask} label="Template Variable Masking" icon={FileText} color="purple" />
                    </div>
                  </div>

                  {/* Custom chars config */}
                  {customCharMask && (
                    <div className="bg-white rounded-xl border border-gray-200 p-5">
                      <h3 className="text-sm font-bold text-gray-800 mb-3">Custom Characters / Words to Mask</h3>
                      <textarea value={customChars} onChange={e => setCustomChars(e.target.value)} rows={2}
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-pink-400"
                        placeholder="Comma-separated: e.g. password, secret, PIN, account" />
                      <p className="text-[10px] text-gray-400 mt-1">These words/characters will be replaced with xxxxxx in SMS content display</p>
                    </div>
                  )}

                  {/* Mobile mask config */}
                  {mobileMask && (
                    <div className="bg-white rounded-xl border border-gray-200 p-5">
                      <h3 className="text-sm font-bold text-gray-800 mb-3">Mobile Number Mask Pattern</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mb-3">
                        {MOBILE_MASK_PRESETS.map((preset, i) => (
                          <button key={i} onClick={() => setMobileMaskPattern(preset.pattern)}
                            className={`p-2.5 rounded-lg border text-left transition-colors ${
                              mobileMaskPattern === preset.pattern
                                ? 'border-red-300 bg-red-50'
                                : 'border-gray-100 hover:border-gray-200'
                            }`}>
                            <div className="text-sm font-mono font-bold text-gray-800">{preset.label}</div>
                            <div className="text-[10px] text-gray-400">{preset.desc}</div>
                          </button>
                        ))}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Custom Pattern (X=show, x=mask)</label>
                        <input value={mobileMaskPattern} onChange={e => setMobileMaskPattern(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:border-red-400"
                          placeholder="XXXxxXxxXX" maxLength={12} />
                        <p className="text-[10px] text-gray-400 mt-1">Preview: {previewMobile(mobileMaskPattern)}</p>
                      </div>
                    </div>
                  )}

                  {/* Template variable masking */}
                  {templateVarMask && (
                    <div className="bg-white rounded-xl border border-gray-200 p-5">
                      <h3 className="text-sm font-bold text-gray-800 mb-3">Template Variable Masking</h3>
                      <p className="text-xs text-gray-500 mb-3">Select DLT templates and specify which variables to mask</p>

                      {/* Existing templates */}
                      {templates.length > 0 && (
                        <div className="space-y-2 mb-4">
                          {templates.map(t => (
                            <div key={t.id} className="flex items-center justify-between p-3 rounded-lg border border-gray-100 bg-gray-50">
                              <div>
                                <div className="text-xs font-medium text-gray-800">{t.template_name || t.template_id}</div>
                                <div className="text-[10px] font-mono text-gray-400">ID: {t.template_id}</div>
                                {t.masked_variables?.length > 0 && (
                                  <div className="flex gap-1 mt-1">
                                    {t.masked_variables.map((v, i) => (
                                      <span key={i} className="text-[9px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">{v}</span>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <button onClick={() => handleDeleteTemplate(t.id)} className="p-1 text-gray-400 hover:text-red-500">
                                <Trash2 size={14} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Add template */}
                      <div className="p-3 rounded-lg border border-dashed border-gray-200 space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <input value={newTemplateId} onChange={e => setNewTemplateId(e.target.value)}
                            className="px-2.5 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:border-purple-400"
                            placeholder="Template ID" />
                          <input value={newTemplateName} onChange={e => setNewTemplateName(e.target.value)}
                            className="px-2.5 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:border-purple-400"
                            placeholder="Template Name (optional)" />
                        </div>
                        <input value={newTemplateVars} onChange={e => setNewTemplateVars(e.target.value)}
                          className="w-full px-2.5 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:border-purple-400"
                          placeholder="Variables to mask (comma-separated): e.g. {#var1#}, {#var2#}" />
                        <button onClick={handleAddTemplate} disabled={!newTemplateId.trim() || addingTemplate}
                          className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-purple-600 bg-purple-50 rounded-lg hover:bg-purple-100 border border-purple-200 disabled:opacity-50">
                          {addingTemplate ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Add Template
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Masking Info */}
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <div className="flex items-start gap-2">
                      <Shield size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs font-medium text-amber-800">Important: GUI-Only Masking</p>
                        <p className="text-[10px] text-amber-700 mt-1">
                          Masking is applied only when displaying SMS content in the admin dashboard and reports.
                          It does NOT affect actual SMS sending, SMPP delivery, or stored data.
                          This ensures zero performance impact on the SMS pipeline.
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Save button */}
              <div className="flex justify-end">
                <button onClick={handleSave} disabled={saving}
                  className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-violet-500 to-indigo-500 text-white text-sm font-medium rounded-lg hover:from-violet-600 hover:to-indigo-600 transition-all disabled:opacity-50 shadow-lg shadow-violet-200">
                  {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                  Save Masking Settings
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MaskingManagement;
